import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { errMsg } from "@/lib/api";
import { useApi, PageHead } from "@/admin/AdminApp";
import { Confirm } from "@/admin/Families";

export default function Organizers({ me }) {
  const api = useApi();
  const [list, setList] = useState(null);
  const [f, setF] = useState({ email: "", name: "", google_only: false });
  const [del, setDel] = useState(null);
  const load = useCallback(() => api("get", "/organizers").then(setList).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await api("post", "/organizers", f);
      toast.success(r.invite_sent ? `Organizer added. A sign-up link was emailed to ${r.email}` : "Organizer added. They can sign in with Google using this email.");
      setF({ email: "", name: "", google_only: false });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const setGoogle = async (o, v) => { try { await api("put", `/organizers/${encodeURIComponent(o.email)}`, { ...o, google_only: v }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const resend = async (o) => {
    try { await api("post", `/organizers/${encodeURIComponent(o.email)}/invite`); toast.success(`Sign-up link emailed to ${o.email}`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;

  return (
    <div className="mx-auto max-w-3xl" data-testid="organizers-page">
      <PageHead title="Organizers" text="People who can open this panel. They sign in with email & password or Google using the email listed here." />
      <form onSubmit={add} className="mb-6 grid gap-3 rounded-2xl border border-[var(--line)] bg-white p-5 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
        <label className="block"><span className="label-lux">Email</span><input type="email" required className="input-lux" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="organizer-email-input" /></label>
        <label className="block"><span className="label-lux">Name</span><input className="input-lux" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="organizer-name-input" /></label>
        <label className="flex items-center gap-2 pb-3 text-sm">Google only <Switch checked={f.google_only} onCheckedChange={(v) => setF({ ...f, google_only: v })} data-testid="organizer-google-only-switch" /></label>
        <button className="btn-ink" data-testid="add-organizer-btn"><Plus size={15} /> Add</button>
      </form>
      <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-white">
        {list.map((o) => (
          <div key={o.email} className="flex items-center gap-3 border-b border-[var(--line)] px-5 py-4 last:border-0" data-testid={`organizer-row-${o.email}`}>
            <div className="flex-1">
              <div>{o.name || o.email}{o.email === me.email && <span className="ml-2 text-xs text-[var(--gold-deep)]">you</span>}
                <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] ${o.signed_up ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={`organizer-status-${o.email}`}>{o.signed_up ? "Account active" : "Invited · not signed up yet"}</span>
              </div>
              {o.name && <div className="text-sm text-slate-500">{o.email}</div>}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">Google only <Switch checked={!!o.google_only} disabled={o.email === me.email} onCheckedChange={(v) => setGoogle(o, v)} data-testid={`organizer-google-${o.email}`} /></label>
            {o.email !== me.email && !o.signed_up && <button onClick={() => resend(o)} className="btn-ghost !px-3 !py-1.5 text-xs" data-testid={`organizer-resend-${o.email}`}><Send size={13} /> Resend sign-up link</button>}
            {o.email !== me.email && <button onClick={() => setDel(o)} className="rounded-full p-2 text-red-700 hover:bg-red-50" data-testid={`organizer-remove-${o.email}`}><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
      <Confirm open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Remove ${del?.email}?`} text="They will no longer be able to open the organizer panel." action="Remove"
        onConfirm={async () => { try { await api("delete", `/organizers/${encodeURIComponent(del.email)}`); load(); } catch (e) { toast.error(errMsg(e)); } setDel(null); }} />
    </div>
  );
}
